import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Users, Crown, UserPlus, Pencil, Trash2, User as UserIcon, Wand2, Loader2 } from 'lucide-react';
import { Permission, type PermissionAtom } from '@kolectra/shared/permissions';
import { languoidEnglishName } from '@kolectra/shared/languoids';
import type { MemberSummary, MembershipStatus, RoleSummary } from '@kolectra/shared/schemas';
import { useWebAuth, useWebPerms } from '../auth.js';
import { adminApi, ApiError } from '../lib/api-client.js';
import { PageHead } from '../components/page-head.js';
import { Modal, ChipToggle, Field, toggled, useConfirm, generatePassword } from '@kolectra/ui';

/**
 * Team administration (web) — the team-lead's console for their own team, live
 * against the admin API. Each member has one role (+ custom permission
 * overrides), the languages they may work on within the team, and a status.
 * Passwords are set on create and can be reset on edit, but are never shown
 * back (they're hashed) — so there's no stored-password reveal.
 */

const prettyPerm = (a: string) => a.replace(/^can_/, '').replace(/_/g, ' ');

interface EditorPayload {
  displayName: string;
  email: string;
  password: string;
  roleId: string;
  languoidCodes: string[];
  customPermissions: string[];
  status: MembershipStatus;
}

export function TeamPage() {
  const { account } = useWebAuth();
  const perms = useWebPerms();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const team = account.team;

  const canManage = perms.has(Permission.CAN_MANAGE_TEAM);

  const membersQuery = useQuery({
    queryKey: ['members', team.id],
    queryFn: () => adminApi.members(team.id),
    enabled: canManage,
  });
  const rolesQuery = useQuery({
    queryKey: ['roles', team.id],
    queryFn: () => adminApi.roles(team.id),
    enabled: canManage,
  });

  const createMut = useMutation({
    mutationFn: (p: EditorPayload) =>
      adminApi.createMember(team.id, {
        displayName: p.displayName,
        email: p.email,
        password: p.password,
        roleId: p.roleId,
        languoidCodes: p.languoidCodes,
        customPermissions: p.customPermissions,
        status: p.status,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['members', team.id] }),
  });
  const updateMut = useMutation({
    mutationFn: ({ userId, p }: { userId: string; p: EditorPayload }) =>
      adminApi.updateMember(team.id, userId, {
        displayName: p.displayName,
        roleId: p.roleId,
        languoidCodes: p.languoidCodes,
        customPermissions: p.customPermissions,
        status: p.status,
        ...(p.password.trim() ? { password: p.password } : {}),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['members', team.id] }),
  });
  const removeMut = useMutation({
    mutationFn: (userId: string) => adminApi.removeMember(team.id, userId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['members', team.id] }),
  });

  const [editing, setEditing] = useState<MemberSummary | null>(null);
  const [adding, setAdding] = useState(false);

  async function remove(m: MemberSummary) {
    if (
      await confirm({
        title: `Remove ${m.displayName ?? m.email}?`,
        message: 'They are removed from the team and lose access.',
        danger: true,
        confirmLabel: 'Remove',
      })
    ) {
      removeMut.mutate(m.userId);
    }
  }

  if (!canManage) {
    return (
      <>
        <PageHead crumb="Team" title="Team administration" lead="Manage your team’s members and language access." />
        <div className="pad">
          <div className="banner warn">Only a team lead can administer the team.</div>
        </div>
      </>
    );
  }

  const members = membersQuery.data ?? [];
  const roles = rolesQuery.data ?? [];
  const saveError = createMut.error ?? updateMut.error;

  return (
    <>
      <PageHead
        crumb={`Team · ${team.name}`}
        title="Team administration"
        lead={
          <>
            Your team works on{' '}
            {team.languoidCodes.map((c) => (
              <b key={c} style={{ color: 'var(--data)' }}>
                {languoidEnglishName(c) ?? c}{' '}
              </b>
            ))}
            . Add members, set their role, and decide what each can work on.
          </>
        }
      />

      <div className="pad" style={{ maxWidth: 920 }}>
        {removeMut.isError && (
          <div className="banner bad" style={{ marginBottom: 12 }}>
            {removeMut.error instanceof ApiError ? removeMut.error.message : 'Couldn’t remove that member.'}
          </div>
        )}

        <div className="toolbar" style={{ marginBottom: 14 }}>
          <Users size={16} strokeWidth={1.8} style={{ color: 'var(--ink-soft)' }} />
          <strong style={{ fontFamily: 'var(--disp)', fontSize: 16, fontWeight: 500 }}>{members.length} members</strong>
          <div className="grow" />
          <button className="btn acc" onClick={() => setAdding(true)} disabled={roles.length === 0}>
            <UserPlus size={15} strokeWidth={2} /> Add member
          </button>
        </div>

        <div className="tablewrap">
          <table className="kolectra">
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Language access</th>
                <th>Status</th>
                <th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {membersQuery.isLoading ? (
                <tr>
                  <td colSpan={5} className="mini" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Loader2 size={14} className="spin" /> Loading…
                  </td>
                </tr>
              ) : membersQuery.isError ? (
                <tr>
                  <td colSpan={5} className="mini" style={{ padding: 16, color: 'var(--bad)' }}>Couldn’t load members.</td>
                </tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={5} className="mini" style={{ padding: 16 }}>No members yet — add one to get started.</td>
                </tr>
              ) : (
                members.map((m) => (
                  <tr key={m.userId}>
                    <td>
                      <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {m.displayName ?? m.email}
                        {m.isLead && (
                          <span title="Team lead" style={{ display: 'inline-flex' }}>
                            <Crown size={13} strokeWidth={2} style={{ color: 'var(--warn)' }} />
                          </span>
                        )}
                      </div>
                      <div className="mini">{m.email}</div>
                    </td>
                    <td>{m.roleName ? <span className="permchip">{m.roleName}</span> : <span className="mini">—</span>}</td>
                    <td>
                      {m.languoidCodes.length === 0 ? (
                        <span className="mini">—</span>
                      ) : (
                        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                          {m.languoidCodes.map((c) => (
                            <span key={c} className="tag lang">
                              {languoidEnglishName(c) ?? c}
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="statepill" style={statusStyle(m.status)}>{m.status.toLowerCase()}</span>
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', gap: 2 }}>
                        <button className="iconbtn" title="Edit" onClick={() => setEditing(m)}>
                          <Pencil strokeWidth={1.8} />
                        </button>
                        <button className="iconbtn danger" title="Remove" onClick={() => void remove(m)}>
                          <Trash2 strokeWidth={1.8} />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(adding || editing) && (
        <MemberEditor
          member={editing}
          roles={roles}
          teamLanguages={team.languoidCodes}
          saving={createMut.isPending || updateMut.isPending}
          error={saveError instanceof ApiError ? saveError.message : saveError ? 'Couldn’t save the member.' : null}
          onClose={() => {
            setAdding(false);
            setEditing(null);
            createMut.reset();
            updateMut.reset();
          }}
          onSave={(p) => {
            const done = () => {
              setAdding(false);
              setEditing(null);
            };
            if (editing) updateMut.mutate({ userId: editing.userId, p }, { onSuccess: done });
            else createMut.mutate(p, { onSuccess: done });
          }}
        />
      )}
    </>
  );
}

function MemberEditor({
  member,
  roles,
  teamLanguages,
  saving,
  error,
  onClose,
  onSave,
}: {
  member: MemberSummary | null;
  roles: RoleSummary[];
  teamLanguages: string[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (p: EditorPayload) => void;
}) {
  const [name, setName] = useState(member?.displayName ?? '');
  const [email, setEmail] = useState(member?.email ?? '');
  const [roleId, setRoleId] = useState(member?.roleId ?? roles[0]?.id ?? '');
  const [languages, setLanguages] = useState<string[]>(member?.languoidCodes ?? []);
  const [status, setStatus] = useState<MembershipStatus>(member?.status ?? 'ACTIVE');
  const [password, setPassword] = useState(member ? '' : generatePassword());
  const [extra, setExtra] = useState<string[]>(member?.customPermissions ?? []);
  const [showCustom, setShowCustom] = useState((member?.customPermissions.length ?? 0) > 0);

  const langOptions = teamLanguages.map((c) => ({ value: c, label: languoidEnglishName(c) ?? c }));
  const permOptions = (Object.values(Permission) as PermissionAtom[]).map((a) => ({ value: a, label: prettyPerm(a) }));

  // Email/password are required to create; on edit, email is fixed and the
  // password is only sent when set (a reset).
  const canSave =
    name.trim() !== '' &&
    roleId !== '' &&
    (member ? true : email.trim() !== '' && password.trim().length >= 8);

  function submit() {
    onSave({
      displayName: name.trim(),
      email: email.trim(),
      password,
      roleId,
      languoidCodes: languages,
      customPermissions: extra,
      status,
    });
  }

  return (
    <Modal
      title={member ? 'Edit member' : 'Add member'}
      subtitle="Set the role, password, and which of the team's languages this member may work on."
      icon={<UserIcon size={17} strokeWidth={2} />}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn pri" onClick={submit} disabled={!canSave || saving}>
            {saving && <Loader2 size={14} className="spin" />} Save
          </button>
        </>
      }
    >
      {error && <div className="banner bad" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="two">
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
        </Field>
        <Field label="Email" hint={member ? 'fixed' : undefined}>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@project.dev" disabled={!!member} />
        </Field>
      </div>

      <Field label="Password" hint={member ? 'leave blank to keep current' : 'set directly — no invite email'}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={member ? '••••••••' : ''}
            style={{ fontFamily: 'var(--mono)' }}
          />
          <button
            type="button"
            className="btn"
            style={{ flex: 'none', padding: '7px 10px' }}
            title="Auto-generate password"
            onClick={() => setPassword(generatePassword())}
          >
            <Wand2 size={15} strokeWidth={1.8} />
          </button>
        </div>
      </Field>

      <div className="two">
        <Field label="Role">
          <select value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value as MembershipStatus)}>
            <option value="ACTIVE">active</option>
            <option value="SUSPENDED">suspended</option>
          </select>
        </Field>
      </div>

      <ChipToggle
        label="Language access"
        options={langOptions}
        selected={languages}
        onToggle={(v) => setLanguages((l) => toggled(l, v))}
      />

      <button type="button" className="btn ghost sm" style={{ marginTop: 4 }} onClick={() => setShowCustom((v) => !v)}>
        {showCustom ? 'Hide' : 'Add'} custom permissions
      </button>
      {showCustom && (
        <div style={{ marginTop: 10 }}>
          <ChipToggle
            label="Custom permissions"
            options={permOptions}
            selected={extra}
            onToggle={(v) => setExtra((p) => toggled(p, v))}
          />
        </div>
      )}
    </Modal>
  );
}

function statusStyle(status: MembershipStatus): { background: string; color: string } {
  return status === 'ACTIVE'
    ? { background: '#e1efe5', color: 'var(--good)' }
    : { background: '#f3e0db', color: 'var(--bad)' };
}
